import { Fragment, FunctionComponent, useMemo } from "react";
import { t } from "i18next";

import { ColorPicker } from "primereact/colorpicker";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";

import { isValidHexColor } from "../../../../utils/functions/normalizeHex";
import { TextMarkdown } from "../../../components/Shared/Components";
import { useSharedDialogState } from "../../../components/Shared/Components/Dialogs/SharedDialog";
import { translationKeys } from "../../../models/app-translation-keys";
import { IBackgroundDialogState } from "./imageBackgroundDialogTypes";

interface IImageBackgroundDialogContentProps {
  strictIsolationGuaranteed: boolean;
  presetList: string[];
}

const featureTranslationKeys = translationKeys.feature.imageGeneration;
const backgroundControlTranslationKeys = featureTranslationKeys.backgroundControl;

export const ImageBackgroundDialogContent: FunctionComponent<IImageBackgroundDialogContentProps> = ({
  strictIsolationGuaranteed,
  presetList,
}: IImageBackgroundDialogContentProps) => {
  const { sharedDialogState, setSharedDialogState } = useSharedDialogState<IBackgroundDialogState>();

  const presetButtons = useMemo(() => {
    const selectedDraftColor = sharedDialogState.draftColor?.toUpperCase();
    const getSelectedAttribute = (presetValue: string) =>
      presetValue.toUpperCase() === selectedDraftColor ? "selected" : "";

    return presetList.map(presetValue => (
      <button
        disabled={!sharedDialogState.draftEnabled}
        key={presetValue}
        type="button"
        data-testid={`image-background-preset-button-${presetValue}`}
        onClick={() => handleDraftColorChange(presetValue, false)}
        title={presetValue}
        aria-label={`Preset ${presetValue}`}
        className={`preset-button ${getSelectedAttribute(presetValue)}`}
        style={{
          background: presetValue,
        }}
      />
    ));
  }, [sharedDialogState.draftColor, sharedDialogState.draftEnabled]);

  const handleDraftColorChange = (colorValue: string | undefined, isColorPickerChange: boolean): void => {
    if (!colorValue) {
      setSharedDialogState(previousState => ({ ...previousState, draftColor: "", isDraftColorInvalid: false }));
      return;
    }

    const colorHex = isColorPickerChange ? `#${colorValue?.toString()}` : colorValue;

    setSharedDialogState(previousState => ({
      ...previousState,
      draftColor: colorHex,
      isDraftColorInvalid: !isValidHexColor(colorHex),
    }));
  };

  return (
    <Fragment>
      <div className="dialog-content">
        <div className="dialog-content-left">
          <div className="selected-color-row">
            <strong>{t(backgroundControlTranslationKeys.selectedLabel)}:</strong>{" "}
            <InputText
              className={classNames("color-code-input", { "p-invalid": sharedDialogState.isDraftColorInvalid })}
              data-testid="image-background-color-code-input"
              value={sharedDialogState.draftEnabled ? sharedDialogState.draftColor : ""}
              onChange={e => handleDraftColorChange(e.target.value, false)}
              placeholder={t(backgroundControlTranslationKeys.noneLabel)}
              disabled={!sharedDialogState.draftEnabled}
            />
            {sharedDialogState.isDraftColorInvalid && (
              <small className="p-error invalid-color">{t(featureTranslationKeys.errors.invalidColor)}</small>
            )}
          </div>

          <div className="presets-row">
            <strong>{t(backgroundControlTranslationKeys.presetsLabel)}:</strong>{" "}
            <div className="presets-grid">{presetButtons}</div>
          </div>
        </div>

        <ColorPicker
          data-testid="image-background-color-picker"
          inline
          value={sharedDialogState.draftColor || "#FFFFFF"}
          onChange={e => handleDraftColorChange(e.value as string, true)}
          format="hex"
          disabled={!sharedDialogState.draftEnabled}
        />
      </div>

      {sharedDialogState.draftEnabled && !strictIsolationGuaranteed && (
        <div className="isolation-hint">
          <TextMarkdown>{t(backgroundControlTranslationKeys.isolationHint)}</TextMarkdown>
        </div>
      )}
    </Fragment>
  );
};
